import { useState, useEffect } from 'react';
import { Modal, Textarea, Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useUpdateServiceOrder } from '@/hooks/api-hooks';
import { ServiceOrder } from '@turizi/shared';
import { notify } from '@/lib/notify';

interface UpdateOrderObsModalProps {
    order: ServiceOrder;
    opened: boolean;
    onClose: () => void;
}

export default function UpdateOrderObsModal({
    order,
    opened,
    onClose,
}: UpdateOrderObsModalProps): JSX.Element {
    const { t } = useTranslation();
    const [observations, setObservations] = useState(order?.observations || '');
    const updateOrderMutation = useUpdateServiceOrder();

    useEffect(() => {
        if (opened) {
            setObservations(order?.observations || '');
        }
    }, [opened, order]);

    const handleSave = async () => {
        try {
            await updateOrderMutation.mutateAsync({
                id: order.id,
                observations: observations,
            });
            notify.success(t('success'), t('observations_updated_successfully', 'Observations updated successfully'));
            onClose();
        } catch (error) {
            notify.error(t('error'), t('failed_to_update_observations', 'Failed to update observations'));
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('update_observations', 'Update Observations')}
            size="md"
            centered
            radius="md"
        >
            <div className="space-y-4">
                <Textarea
                    value={observations}
                    onChange={(event) => setObservations(event.currentTarget.value)}
                    placeholder={t('enter_observations', 'Enter observations...')}
                    minRows={4}
                    autosize
                />
                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" color="gray" onClick={onClose} radius="md">
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        loading={updateOrderMutation.isPending}
                        color="black"
                        radius="md"
                    >
                        {t('save')}
                    </Button>
                </Group>
            </div>
        </Modal>
    );
}
